import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Credentials, EditOtAgentMutationVariables, OtAgentDetailsFragment } from '@uigy/generated/graphql';
import { IconsEnum } from '@uigy/generated/icons.enum';
import {
  ActiveQueriesDataHelperService,
} from '@uigy/modules/active-queries/services/active-queries-data-helper.service';
import { ActiveQueriesService } from '@uigy/modules/active-queries/services/active-queries.service';
import { CredentialsService } from '@uigy/modules/credentials/services/credentials.service';
import { DuplicatedNetworksService } from '@uigy/modules/settings/modules/assets/services/duplicated-networks.service';
import { MessageBlockComponent } from '@uigy/shared/components/message-block/message-block.component';
import { GenericSliderPage } from '@uigy/shared/components/slider-v2/models/generic-slider-page';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { ValidationMessageComponent } from '@uigy/shared/components/validation-message/validation-message.component';
import { ValidateIp } from '@uigy/shared/form-validators/ip.validator';
import { MaterialModule } from '@uigy/shared/material-module/material.module';
import { MenuItem } from '@uigy/shared/models/interfaces';
import { FormValidatorsService } from '@uigy/shared/services/form-validators.service';
import { LanguageService } from '@uigy/shared/services/language.service';
import { CheckboxComponent } from '@uigy/shared/components/checkbox/checkbox.component';
import {
  FormFrequencyEditorComponent,
} from '@uigy/shared/components/dynamic-form/form-components/form-frequency-editor/form-frequency-editor.component';
import {
  FormToggleBoxComponent,
} from '@uigy/shared/components/dynamic-form/form-components/form-toggle-box/form-toggle-box.component';
import { IconComponent } from '@uigy/shared/components/icons/icon/icon.component';
import { UiSelectComponent } from '@uigy/shared/components/ui-inputs/ui-select/ui-select.component';
import Utils from '@uigy/utils';
import { I18NextModule } from 'angular-i18next';
import { forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  standalone: true,
  imports: [
    MaterialModule,
    I18NextModule,
    UiSelectComponent,
    FormToggleBoxComponent,
    FormFrequencyEditorComponent,
    MessageBlockComponent,
    ValidationMessageComponent,
    CheckboxComponent,
    IconComponent,
    ReactiveFormsModule,
  ],
  selector: 'app-ot-agent-configure-slider',
  templateUrl: './ot-agent-configure-slider.component.html',
  styleUrl: './ot-agent-configure-slider.component.scss'
})
export class OtAgentConfigureSliderComponent implements GenericSliderPage<OtAgentDetailsFragment, EditOtAgentMutationVariables | undefined> {
  private duplicatedNetworksService = inject(DuplicatedNetworksService);
  private languageService = inject(LanguageService);
  private credentialsService = inject(CredentialsService);
  protected formValidatorsService = inject(FormValidatorsService);
  private activeQueriesService = inject(ActiveQueriesService);
  private activeQueriesDataHelperService = inject(ActiveQueriesDataHelperService);

  public agentId: string = '';
  public showDuplicateNetworkElements: boolean = false;
  public duplicateNetworksText: string = '';
  public credentialsOptions?: MenuItem[];
  public readonly errorTexts = this.languageService.errorTexts;
  public readonly otAgentsTexts = this.languageService.texts.ot_agents;
  public readonly IconsEnum = IconsEnum;
  public isLoaded = false;
  protected readonly toasterType = ToasterType;


  public form: FormGroup = new FormGroup({
    scanDuplicatedNetworks: new FormControl(true),
    name: new FormControl('', [Validators.required]),
    activeNetworks: new FormControl('', [ValidateIp]),
    enableSchedule: new FormControl(false),
    schedule: new FormControl(),
    credentials: new FormControl(),
  });

  public showRestrictionsNotification = false;
  public restrictionsMessage = this.otAgentsTexts.restrictions_message;

  constructor() {
  }

  public getData(): EditOtAgentMutationVariables {
    return {
      id: this.agentId,
      name: this.name?.value,
      networks: Utils.multiLineTextToArray(this.activeNetworks?.value),
      enable: this.enableSchedule?.value,
      credentials: this.credentials?.value,
      scanDuplicatedNetworks: this.scanDuplicatedNetworks?.value,
      schedule: this.schedule?.value
    };
  }

  public initData(data: OtAgentDetailsFragment): void {
    this.agentId = data.id;
    this.handleScheduleFormControls(data);

    this.form.patchValue({
      name: data.name,
      activeNetworks: Utils.arrayToMultiLineText(data.networks ?? []),
      enableSchedule: data.scheduleEnabled,
      credentials: Utils.stripConnection(data.credentials).map(cred => cred.id),
      scanDuplicatedNetworks: data.scanDuplicatedNetworks,
      schedule: data.schedule,
    });

    forkJoin([
      this.duplicatedNetworksService.getSensorsDuplicateNetwork(data.networkArea?.id ?? ''),
      this.credentialsService.getCredentialsList()]).subscribe(([duplicatedNetworks, credentials]) => {
      if (duplicatedNetworks.data.networkArea.networks && duplicatedNetworks.data.networkArea.networks.length > 0) {
        this.showDuplicateNetworkElements = true;
        this.duplicateNetworksText = duplicatedNetworks.data.networkArea.networks.join('; ');
      }
      this.credentialsOptions = credentials.map((credential: Credentials) => ({label: credential.name, value: credential.id}));
    });

    this.activeQueriesService.getActiveQueriesGlobalConfig().pipe(
      tap((globalConfig) => {
        this.showRestrictionsNotification = this.activeQueriesDataHelperService.isRestrictionsExist(globalConfig) ?? false;
      })).subscribe();
  }

  // Schedule control must be disabled when scheduleEnabled = false
  private handleScheduleFormControls(data: OtAgentDetailsFragment): void {

    if (!data.scheduleEnabled) {
      this.schedule?.disable({emitEvent: false, onlySelf: true});
    }

    this.enableSchedule?.valueChanges.subscribe((isEnabled: boolean) => {
      if (isEnabled) {
        this.schedule?.enable({emitEvent: false, onlySelf: true});
      } else {
        this.schedule?.disable({emitEvent: false, onlySelf: true});
      }
    });
  }

  public isValid(): boolean {
    return this.form.valid;
  }

  public get activeNetworks(): FormControl {
    return this.form.get('activeNetworks') as FormControl;
  }

  public get enableSchedule(): FormControl {
    return this.form.get('enableSchedule') as FormControl;
  }

  public get name(): FormControl {
    return this.form.get('name') as FormControl;
  }

  public get schedule(): FormControl {
    return this.form.get('schedule') as FormControl;
  }

  public get credentials(): FormControl {
    return this.form.get('credentials') as FormControl;
  }

  public get scanDuplicatedNetworks(): FormControl {
    return this.form.get('scanDuplicatedNetworks') as FormControl;
  }
}
